(() => {
  const app = window.Digitaliz;
  if (!app) return;
  window.DigitalizModules = window.DigitalizModules || {};
  if (window.DigitalizModules.productForm) return;
  window.DigitalizModules.productForm = true;

  function matchVariant(variants, selectedValues) {
    return variants.find((variant) =>
      variant.options.every((value, index) => value === selectedValues[index])
    );
  }

  function syncForm(container) {
    const form = container.closest("form");
    if (!form) return;
    const variantDataNode = container.querySelector("[data-variants-json]");
    if (!variantDataNode) return;

    let variants = [];
    try {
      variants = JSON.parse(variantDataNode.textContent);
    } catch (error) {
      return;
    }

    // Per-variant "buyable" map (minimum-quantity rule): a variant counts as
    // available only if at least the minimum quantity can be purchased.
    // Variants short of that are treated as out of stock. Falls back to the
    // variant's own `available` flag when the map is missing.
    let buyableMap = {};
    const buyableNode = container.querySelector("[data-variants-buyable]");
    if (buyableNode) {
      try {
        buyableMap = JSON.parse(buyableNode.textContent);
      } catch (error) {
        buyableMap = {};
      }
    }
    const isBuyable = (variant) => {
      if (!variant) return false;
      const flag = buyableMap[variant.id];
      return flag === undefined ? Boolean(variant.available) : Boolean(flag);
    };

    const optionGroups = [...container.querySelectorAll("[data-option-group]")];
    const optionInputs = [...container.querySelectorAll("[data-option-input]")];
    const optionValueInputs = [...container.querySelectorAll("[data-option-value]")];
    const variantIdInput = container.querySelector("[data-variant-id-input]");
    const submitButton = container.querySelector("[data-add-to-cart]");
    const priceNode = container.querySelector("[data-product-price]");
    const comparePriceNode = container.querySelector("[data-product-compare-price]");
    const quantityInput = container.querySelector("input[name='quantity']");
    const statusNode = container.querySelector("[data-variant-status]");
    const unitPriceNode = container.querySelector("[data-product-unit-price]");
    const skuNode = container.querySelector("[data-product-sku]");
    const sellingPlanInput = container.querySelector("[data-selling-plan-input]");
    const dynamicCheckoutWrapper = container.querySelector("[data-dynamic-checkout-wrapper]");

    if (!variantIdInput || !submitButton) return;

    const labels = {
      add: container.dataset.labelAdd || window.themeStrings?.addToCart || "Add to cart",
      soldOut: container.dataset.labelSoldOut || window.themeStrings?.soldOut || "Sold out",
      unavailable: container.dataset.labelUnavailable || window.themeStrings?.unavailable || "Unavailable",
      inStock: container.dataset.labelInStock || window.themeStrings?.inStock || "In stock",
      outOfStock: container.dataset.labelOutOfStock || window.themeStrings?.outOfStock || "Out of stock",
      selectPlan: container.dataset.labelSelectPlan || "Select purchase option"
    };

    // Marks individual option inputs as unavailable when they cannot form any
    // available variant given the currently selected values for all other groups.
    function markUnavailableOptions(selectedValues) {
      optionGroups.forEach((group, groupIndex) => {
        const inputs = [...group.querySelectorAll("[data-option-value]")];
        inputs.forEach((input) => {
          const testValues = selectedValues.slice();
          testValues[groupIndex] = input.value;
          const reachable = variants.some(
            (v) => isBuyable(v) && v.options.every((val, i) => val === testValues[i])
          );
          input.classList.toggle("is-unavailable", !reachable);
          input.setAttribute("aria-disabled", String(!reachable));
          // Disable native input only for radio/checkbox; selects use option.disabled
          if (input.tagName === "INPUT") {
            input.disabled = !reachable;
          }
        });

        // Also disable individual <option> elements inside any <select>
        const select = group.querySelector("select[data-option-value]");
        if (select) {
          [...select.options].forEach((opt) => {
            const testValues = selectedValues.slice();
            testValues[groupIndex] = opt.value;
            const reachable = variants.some(
              (v) => isBuyable(v) && v.options.every((val, i) => val === testValues[i])
            );
            opt.disabled = !reachable;
          });
        }
      });
    }

    function renderUnitPrice(node, variant) {
      if (!variant || typeof variant.unit_price !== "number" || !variant.unit_price_measurement) {
        node.textContent = "";
        return;
      }
      const measurement = variant.unit_price_measurement;
      const referenceValue = Number(measurement.reference_value) || 1;
      const referenceUnit = measurement.reference_unit || "";
      const suffix = ` / ${referenceValue === 1 ? "" : `${referenceValue}`}${referenceUnit}`;
      app.setCurrency(node, variant.unit_price, { after: suffix.trimEnd() });
    }

    function syncSellingPlans(matched) {
      if (!sellingPlanInput) return { requiresPlan: false, hasSelectedPlan: false };

      const allocations = Array.isArray(matched?.selling_plan_allocations) ? matched.selling_plan_allocations : [];
      const allowedIds = new Set(allocations.map((allocation) => String(allocation.selling_plan_id)));
      const planOptions = [...sellingPlanInput.querySelectorAll("option[data-selling-plan-id]")];

      if (!planOptions.length) {
        sellingPlanInput.disabled = true;
        sellingPlanInput.value = "";
      } else if (!allowedIds.size) {
        sellingPlanInput.disabled = true;
        sellingPlanInput.value = "";
        planOptions.forEach((option) => {
          option.disabled = true;
        });
      } else {
        sellingPlanInput.disabled = false;
        planOptions.forEach((option) => {
          const isAllowed = allowedIds.has(option.value);
          option.disabled = !isAllowed;
        });
        if (sellingPlanInput.value && !allowedIds.has(sellingPlanInput.value)) {
          sellingPlanInput.value = "";
        }
      }

      const requiresPlan = Boolean(matched?.requires_selling_plan);
      const hasSelectedPlan = Boolean(sellingPlanInput.value);
      return { requiresPlan, hasSelectedPlan };
    }

    const update = () => {
      let matched;
      let selectedValues = [];

      if (!optionInputs.length) {
        if (optionGroups.length) {
          selectedValues = optionGroups.map((group) => {
            const checkedRadio = group.querySelector("input[type='radio'][data-option-value]:checked");
            if (checkedRadio) return checkedRadio.value;
            const select = group.querySelector("select[data-option-value]");
            if (select) return select.value;
            return "";
          });
        }

        if (selectedValues.length && selectedValues.every((value) => value !== "")) {
          matched = matchVariant(variants, selectedValues);
        } else {
          const currentId = Number(variantIdInput.value);
          matched =
            variants.find((variant) => Number(variant.id) === currentId) ||
            variants.find((variant) => isBuyable(variant)) ||
            variants.find((variant) => variant.available) ||
            variants[0];
          if (matched) selectedValues = matched.options.slice();
        }
      } else {
        selectedValues = optionInputs.map((input) => input.value);
        matched = matchVariant(variants, selectedValues);
      }

      if (selectedValues.length === optionGroups.length) {
        markUnavailableOptions(selectedValues);
      }

      if (!matched) {
        submitButton.disabled = true;
        submitButton.classList.add("is-disabled");
        submitButton.setAttribute("aria-disabled", "true");
        submitButton.textContent = labels.unavailable;
        if (quantityInput) quantityInput.disabled = true;
        if (statusNode) statusNode.textContent = labels.unavailable;
        return;
      }

      variantIdInput.value = matched.id;

      // "buyable" folds the minimum-quantity rule into availability: a variant
      // that cannot meet the minimum is presented exactly like a sold-out one.
      const buyable = isBuyable(matched);

      const planState = syncSellingPlans(matched);
      const requiresPlanSelection = buyable && planState.requiresPlan && !planState.hasSelectedPlan;

      submitButton.disabled = !buyable || requiresPlanSelection;
      submitButton.classList.toggle("is-disabled", !buyable || requiresPlanSelection);
      submitButton.setAttribute("aria-disabled", buyable && !requiresPlanSelection ? "false" : "true");
      submitButton.textContent = !buyable ? labels.soldOut : requiresPlanSelection ? labels.selectPlan : labels.add;
      if (quantityInput) quantityInput.disabled = !buyable || requiresPlanSelection;
      if (statusNode) {
        statusNode.textContent = !buyable
          ? labels.outOfStock
          : requiresPlanSelection
            ? labels.selectPlan
            : labels.inStock;
      }

      if (dynamicCheckoutWrapper) {
        dynamicCheckoutWrapper.classList.toggle("d-none", !buyable || matched.requires_selling_plan);
      }

      if (priceNode && typeof matched.price === "number") {
        app.setCurrency(priceNode, matched.price);
      }

      if (comparePriceNode) {
        const hasCompare = typeof matched.compare_at_price === "number" && matched.compare_at_price > matched.price;
        comparePriceNode.style.display = hasCompare ? "" : "none";
        if (hasCompare) {
          app.setCurrency(comparePriceNode, matched.compare_at_price);
        } else {
          comparePriceNode.textContent = "";
        }
      }

      if (unitPriceNode) {
        renderUnitPrice(unitPriceNode, matched);
      }

      if (skuNode) {
        skuNode.textContent = matched.sku || "";
      }

      if (window.history?.replaceState && matched.id) {
        const url = new URL(window.location.href);
        url.searchParams.set("variant", matched.id);
        window.history.replaceState({}, "", url.toString());
      }

      app.emit(app.events.VARIANT_CHANGE, { form, container, variant: matched, variants });
    };

    optionInputs.forEach((input) => input.addEventListener("change", update));
    optionValueInputs.forEach((input) => input.addEventListener("change", update));
    if (sellingPlanInput) sellingPlanInput.addEventListener("change", update);
    update();
  }

  function init() {
    document.querySelectorAll("[data-product-form]").forEach((container) => {
      if (container.dataset.variantSyncBound === "true") return;
      container.dataset.variantSyncBound = "true";
      syncForm(container);
    });
  }

  document.addEventListener("DOMContentLoaded", init);
  document.addEventListener("shopify:section:load", init);
})();
